import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';

function BookingPage() {
  const [services, setServices] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedStylist, setSelectedStylist] = useState('');
  const [stylistAvailability, setStylistAvailability] = useState({});
  const [newClientName, setNewClientName] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [timeBlocks, setTimeBlocks] = useState([]);

  useEffect(() => {
    fetchServices();
    fetchStylists();
  }, []);

  useEffect(() => {
    if (date && selectedService && selectedStylist) {
      fetchAppointmentsForDate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedService, selectedStylist]);

  useEffect(() => {
    if (selectedStylist) {
      fetchStylistAvailability(selectedStylist);
    } else {
      setStylistAvailability({});
    }
  }, [selectedStylist]);

  const fetchServices = async () => {
    // Fetch services without auth header (public endpoint)
    const response = await axios.get(`${API_URL}/services`, {
      headers: { Authorization: '' }
    });
    setServices(response.data);
  };

  const fetchStylists = async () => {
    try {
      const response = await axios.get(`${API_URL}/stylists`, {
        headers: { Authorization: '' }
      });
      setStylists(response.data);
    } catch (error) {
      console.error('Error fetching stylists:', error);
    }
  };

  const fetchStylistAvailability = async (stylistId) => {
    try {
      const response = await axios.get(`${API_URL}/stylists/${stylistId}/availability`, {
        headers: { Authorization: '' }
      });
      const availability = response.data;

      // Convert availability to a map by day of week
      const availabilityMap = {};
      availability.forEach(slot => {
        if (!availabilityMap[slot.day_of_week]) {
          availabilityMap[slot.day_of_week] = [];
        }
        availabilityMap[slot.day_of_week].push(slot);
      });
      setStylistAvailability(availabilityMap);
    } catch (error) {
      console.error('Error fetching stylist availability:', error);
    }
  };

  // Format stylist name as "FirstName LastInitial"
  const formatStylistName = (fullName) => {
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) {
      return parts[0];
    }
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastInitial}.`;
  };

  // Check if a date is available based on stylist availability
  const isDateAvailable = (dateString) => {
    if (!selectedStylist || Object.keys(stylistAvailability).length === 0) {
      return true;
    }

    const dayOfWeek = new Date(dateString + 'T00:00:00').getDay();
    return stylistAvailability[dayOfWeek] && stylistAvailability[dayOfWeek].length > 0;
  };

  const fetchAppointmentsForDate = async () => {
    try {
      // Fetch appointments and time blocks in parallel
      const [appointmentsRes, timeBlocksRes] = await Promise.all([
        axios.get(`${API_URL}/appointments/by-date/${date}`, {
          headers: { Authorization: '' }
        }),
        axios.get(`${API_URL}/time-blocks/by-date/${date}`, {
          headers: { Authorization: '' }
        })
      ]);

      setAppointments(appointmentsRes.data);
      setTimeBlocks(timeBlocksRes.data);
      generateAvailableSlots(appointmentsRes.data, timeBlocksRes.data);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setAppointments([]);
      setTimeBlocks([]);
      generateAvailableSlots([], []);
    }
  };

  const generateAvailableSlots = (bookedAppointments, blockedTimes) => {
    const selectedSvc = services.find(s => s.id === parseInt(selectedService));
    if (!selectedSvc) {
      setAvailableSlots([]);
      return;
    }

    const serviceDuration = selectedSvc.duration;
    const slots = [];

    // Get stylist availability for the selected date
    const dayOfWeek = new Date(date + 'T00:00:00').getDay();
    const dayAvailability = selectedStylist && stylistAvailability[dayOfWeek] ? stylistAvailability[dayOfWeek] : null;

    // If stylist is selected but not available on this day, no slots available
    if (selectedStylist && (!dayAvailability || dayAvailability.length === 0)) {
      setAvailableSlots([]);
      return;
    }

    // Generate slots from 8 AM to 8 PM in 15-minute intervals
    for (let hour = 8; hour < 20; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const slotTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

        // Check if slot is within stylist availability
        if (dayAvailability) {
          const isWithinStylistHours = dayAvailability.some(slot => {
            return slotTime >= slot.start_time && slotTime < slot.end_time;
          });

          if (!isWithinStylistHours) {
            continue; // Skip this slot if stylist not available
          }
        }

        // Check if this slot conflicts with any booked appointments for this stylist
        const isBooked = bookedAppointments.some(apt => {
          if (apt.status === 'cancelled') return false; // Ignore cancelled appointments

          // Only check conflicts with same stylist's appointments
          if (selectedStylist && apt.stylist_id && apt.stylist_id !== parseInt(selectedStylist)) {
            return false;
          }

          const aptStart = apt.appointment_time;
          const [aptHour, aptMin] = aptStart.split(':').map(Number);
          const aptStartMinutes = aptHour * 60 + aptMin;
          const aptEndMinutes = aptStartMinutes + apt.service_duration;

          const [slotHour, slotMin] = slotTime.split(':').map(Number);
          const slotStartMinutes = slotHour * 60 + slotMin;
          const slotEndMinutes = slotStartMinutes + serviceDuration;

          // Check for overlap
          return (slotStartMinutes < aptEndMinutes && slotEndMinutes > aptStartMinutes);
        });

        if (isBooked) {
          continue; // Skip this slot if it's already booked
        }

        // Check if this slot conflicts with any time blocks
        const isBlocked = blockedTimes.some(block => {
          // Check if block applies to all stylists or the selected stylist
          if (block.stylist_id && block.stylist_id !== parseInt(selectedStylist)) {
            return false; // This block is for a different stylist
          }

          const [blockStartHour, blockStartMin] = block.start_time.split(':').map(Number);
          const [blockEndHour, blockEndMin] = block.end_time.split(':').map(Number);
          const blockStartMinutes = blockStartHour * 60 + blockStartMin;
          const blockEndMinutes = blockEndHour * 60 + blockEndMin;

          const [slotHour, slotMin] = slotTime.split(':').map(Number);
          const slotStartMinutes = slotHour * 60 + slotMin;
          const slotEndMinutes = slotStartMinutes + serviceDuration;

          // Check for overlap
          return (slotStartMinutes < blockEndMinutes && slotEndMinutes > blockStartMinutes);
        });

        if (!isBlocked) {
          slots.push(slotTime);
        }
      }
    }

    setAvailableSlots(slots);
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setNewClientPhone(formatted);
  };

  const validatePhoneNumber = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone number
    if (!validatePhoneNumber(newClientPhone)) {
      alert('Please enter a valid 10-digit U.S. phone number');
      return;
    }

    try {
      let clientId;

      // Check if client exists by phone number
      // Note: Explicitly remove auth header for public booking
      const findResponse = await axios.post(`${API_URL}/clients/find-by-phone`, {
        phone: newClientPhone
      }, {
        headers: { Authorization: '' }
      });

      if (findResponse.data.found) {
        // Use existing client
        clientId = findResponse.data.client.id;
      } else {
        // Create new client
        const clientResponse = await axios.post(`${API_URL}/clients`, {
          name: newClientName,
          phone: newClientPhone,
          email: newClientEmail,
          notes: ''
        }, {
          headers: { Authorization: '' }
        });
        clientId = clientResponse.data.id;
      }

      // Create appointment without auth header to ensure it goes into pending status
      await axios.post(`${API_URL}/appointments`, {
        client_id: clientId,
        service_id: selectedService,
        appointment_date: date,
        appointment_time: time,
        notes,
        stylist_id: selectedStylist
      }, {
        headers: { Authorization: '' }
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        // Reset form
        setSelectedService('');
        setSelectedStylist('');
        setNewClientName('');
        setNewClientPhone('');
        setNewClientEmail('');
        setDate('');
        setTime('');
        setNotes('');
        setAvailableSlots([]);
      }, 3000);
    } catch (err) {
      alert('Failed to book appointment: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="booking-page">
      <nav className="public-nav">
        <img src="/images/logo.png" alt="Holland House Salon" className="salon-logo"/>
        <h1>Book Appointment</h1>
        <div className="public-nav-links">
          <Link to="/portfolio" className="portfolio-link">View Portfolio</Link>
        </div>
      </nav>
      <div className="booking-container">
        {success ? (
          <div className="success-message">
            <h2>✓ Booking Reserved!</h2>
            <p>Your appointment request has been received. A confirmation will be sent to you shortly.</p>
          </div>
        ) : !selectedService ? (
          <div className="service-selection">
            <h2>Choose Your Service</h2>
            <div className="service-gallery">
              {services.map(service => (
                <div
                  key={service.id}
                  className="service-gallery-card"
                  onClick={() => setSelectedService(service.id)}
                >
                  <div className="service-image-wrapper">
                    <img
                      src={service.image_url || '/images/services/placeholder.svg'}
                      alt={service.name}
                      onError={(e) => {
                        e.target.src = '/images/services/placeholder.svg';
                      }}
                      className="service-image"
                    />
                  </div>
                  <div className="service-info">
                    <h3>{service.name}</h3>
                    <p className="service-description">{service.description}</p>
                    <div className="service-meta">
                      <span className="service-price">${service.price}{service.starting_at_price ? '+' : ''}</span>
                      <span className="service-duration">{service.duration} min</span>
                    </div>
                  </div>
                  <button type="button" className="select-service-btn">Select Service</button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="booking-form-wrapper">
            <div className="selected-service-header">
              <div>
                <h3>Booking: {services.find(s => s.id === parseInt(selectedService))?.name}</h3>
                <p className="service-details-text">
                  ${services.find(s => s.id === parseInt(selectedService))?.price}{services.find(s => s.id === parseInt(selectedService))?.starting_at_price ? '+' : ''} • {services.find(s => s.id === parseInt(selectedService))?.duration} min
                </p>
              </div>
              <button
                type="button"
                className="change-service-btn"
                onClick={() => {
                  setSelectedService('');
                  setSelectedStylist('');
                  setDate('');
                  setTime('');
                  setAvailableSlots([]);
                }}
              >
                Change Service
              </button>
            </div>
            <form onSubmit={handleSubmit} className="booking-form">
              <div className="form-group">
                <label>Your Name</label>
                <input
                  type="text"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={newClientPhone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 555-5555"
                  required
                  maxLength="14"
                />
              </div>

              <div className="form-group">
                <label>Email (Optional)</label>
                <input
                  type="email"
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>

              <div className="form-group">
                <label>Select Your Stylist</label>
                <select
                  value={selectedStylist}
                  onChange={(e) => {
                    setSelectedStylist(e.target.value);
                    setDate('');
                    setTime('');
                    setAvailableSlots([]);
                  }}
                  required
                >
                  <option value="">Choose a stylist...</option>
                  {stylists.map(stylist => (
                    <option key={stylist.id} value={stylist.id}>
                      {formatStylistName(stylist.name)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  {!selectedStylist ? (
                    <input
                      type="date"
                      disabled
                      required
                      placeholder="Select stylist first"
                    />
                  ) : (
                    <>
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          if (selectedDate) {
                            const dayOfWeek = new Date(selectedDate + 'T00:00:00').getDay();
                            const dayAvailability = stylistAvailability[dayOfWeek];

                            if (!dayAvailability || dayAvailability.length === 0) {
                              alert('The selected stylist is not available on this day. Please choose a different date.');
                              setDate('');
                              setTime('');
                              setAvailableSlots([]);
                            } else {
                              setDate(selectedDate);
                            }
                          } else {
                            setDate('');
                          }
                        }}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                      {Object.keys(stylistAvailability).length > 0 && (
                        <small style={{display: 'block', marginTop: '5px', color: '#666', fontSize: '0.85em'}}>
                          Available: {Object.keys(stylistAvailability).map(day => {
                            const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                            return days[parseInt(day)];
                          }).join(', ')}
                        </small>
                      )}
                    </>
                  )}
                </div>

                <div className="form-group">
                  <label>Time</label>
                  {!date ? (
                    <select disabled required>
                      <option value="">Select date first</option>
                    </select>
                  ) : availableSlots.length === 0 ? (
                    <select disabled required>
                      <option value="">No available slots</option>
                    </select>
                  ) : (
                    <select
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      required
                    >
                      <option value="">Choose a time...</option>
                      {availableSlots.map(slot => {
                        const [hour, minute] = slot.split(':').map(Number);
                        const period = hour >= 12 ? 'PM' : 'AM';
                        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                        const displayTime = `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
                        return (
                          <option key={slot} value={slot}>
                            {displayTime}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special requests or notes..."
                  rows="3"
                />
              </div>

              <button type="submit" className="submit-btn">Book Appointment</button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingPage;
